<template>
  <div data-locator="q-and-a-page">
    <h1>{{ $t('project.views.QuestionAnswerList.title') }}</h1>

    <v-container :fluid="true"
                 class="pa-0"
                 :class="{ 'px-1': $vuetify.display.mdAndDown }"
    >
      <template v-if="noData">
        <QuestionAnswerTopBar
          :mnemo="roomMnemo"
          v-model:search-query="searchQuery"
          disabled
          class="my-8"
          @open-import-excel-dialog="importDialogIsOpen = true"
        />

        <app-empty-state
          class="empty-state-container"
          module="question-answer"
          size="md"
          :translation-key-prefix="TRANSLATION_KEY_PREFIX"
          :force-default-translation="isCurrentUserPm || currentUserRights.canAddQuestions"
        />

      </template>
      <template v-else-if="initialDataLoading">
        <AppDataTableSkeletonLoader class="mt-8"/>
      </template>
      <template v-else-if="dataReady">
        <QuestionAnswerListHeader />

        <QuestionAnswerTopBar :mnemo="roomMnemo"
                              v-model:search-query="searchQuery"
                              class="my-8"
                              @open-import-excel-dialog="importDialogIsOpen = true"
        />

        <QuestionAnswerMultiSelectBar v-if="shouldMultiSelectBarBeVisible"
                                      class="mb-8"
                                      :selection="selectedQuestions"
                                      @deselectAll="selectedQuestions = []"
        />
        <QuestionAnswerDataTable v-model:search-query="searchQuery"
                                 v-model:selected-questions="selectedQuestions"
                                 @update:selected-questions="selectedQuestions = $event"
        />
      </template>

      <QuestionAnswerFiltersPane :mnemo="roomMnemo"/>
    </v-container>

    <QuestionAnswerImportDialog v-if="importDialogIsOpen"
                                :mnemo="roomMnemo"
                                @close="importDialogIsOpen = false"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import AppDataTableSkeletonLoader from '@/common/AppDataTableSkeletonLoader.vue'
import AppEmptyState from '@/common/AppEmptyState.vue'
import { getRoomRouteType } from '@/common/utils/rooms'
import { toast } from '@/common/utils/toast'
import QuestionAnswerDataTable from '@/project/question-answer/QuestionAnswerDataTable.vue'
import QuestionAnswerFiltersPane from '@/project/question-answer/QuestionAnswerFiltersPane.vue'
import QuestionAnswerImportDialog from '@/project/question-answer/QuestionAnswerImportDialog.vue'
import QuestionAnswerListHeader from '@/project/question-answer/QuestionAnswerListHeader.vue'
import QuestionAnswerMultiSelectBar from '@/project/question-answer/QuestionAnswerMultiSelectBar.vue'
import QuestionAnswerTopBar from '@/project/question-answer/QuestionAnswerTopBar.vue'
import { ROOM_MEMBERS_ROUTE_NAME } from '@/router'
import { QuestionAnswerActionTypes } from '@/store/modules/question-answer/action_types'

const TRANSLATION_KEY_PREFIX = 'project.question-answer.QuestionAnswerList'

export default {
  name: 'QuestionAnswerList',
  components: {
    AppEmptyState,
    AppDataTableSkeletonLoader,
    QuestionAnswerDataTable,
    QuestionAnswerFiltersPane,
    QuestionAnswerImportDialog,
    QuestionAnswerListHeader,
    QuestionAnswerMultiSelectBar,
    QuestionAnswerTopBar,
  },
  data () {
    return {
      searchQuery: '',
      importDialogIsOpen: false,
      selectedQuestions: [],
      TRANSLATION_KEY_PREFIX,
    }
  },
  head () {
    return {
      title: this.$t('project.views.QuestionAnswerList.title'),
    }
  },
  computed: {
    ...mapState('questionAnswer', ['questions', 'questionsPending']),
    ...mapState('room', ['currentRoom']),
    ...mapGetters('room', ['isCurrentUserPm', 'roomMnemo', 'currentUserRights']),
    noData () {
      return this.questions && this.questions.questions.length === 0
    },
    initialDataLoading () {
      return this.questionsPending && !this.questions
    },
    dataReady () {
      return this.questions && !this.initialDataLoading
    },
    shouldMultiSelectBarBeVisible () {
      return this.isCurrentUserPm && this.selectedQuestions.length
    },
  },
  created () {
    if (!this.currentRoom.isDataroom) {
      this.$router.replace({ name: ROOM_MEMBERS_ROUTE_NAME, params: { roomType: getRoomRouteType(this.currentRoom) } })
    }
    this.prepareGetQuestions()
  },
  methods: {
    ...mapActions('questionAnswer', [QuestionAnswerActionTypes.GET_QUESTIONS]),
    async prepareGetQuestions () {
      try {
        await this.GET_QUESTIONS(this.roomMnemo)
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.views.QuestionAnswerList.getQuestionsError'))
      }
    },
  },
}
</script>

<style scoped lang="scss">
.empty-state-container {
  margin-top: 88px;
}
</style>
